/*
 * (c) Copyright, 2023, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { Button, FormGroup, OverlayTrigger, Popover } from 'react-bootstrap'
import cloneDeep from 'lodash/cloneDeep'
import moment from 'moment'
import { DatePicker } from '@mdsol/lego'
import { Formik, yupToFormErrors } from 'formik'
import * as yup from 'yup'
import { useServiceContext } from '~/root'
import { getDetails, renameFavorite, changeFSIDetails } from '~/services/api/files'
import Utils from '~/lib/Utils'
import { Select } from '../Select'
import { InfiniteScrollSelect } from '../InfiniteScrollSelect'
import { AclLevel } from '~/constants/fsi'
import Field from '~/components/shared/Field'
import ErrorMessage from '~/components/ErrorMessage'
import Link from '~/components/common/Link'
import TextLinkButton from '~/components/TextLinkButton'
import { parseQCDocStatus } from '~/hooks/useCheckDocAccessInQC'
import { confirmModal } from '~/utils/confirmModal'
import { textsFactory } from '~/services/factory/textsFactory'
import Modal from '../Modal'
import DueDateIndicator from '../DueDateIndicator'
import { formatUserDateTime, getMomentDateFormat, getDatePickerParseFormats } from '~/utils/dateFormatting'
import DocumentModalEdit from '~/screens/settings/document-types/Actions/ModalEdit'
import FolderModalEdit from '~/screens/settings/folder-types/Actions/ModalEdit'
import { useModal } from '~/contexts/ModalContext'
import LinkedFilesModal from './LinkedFilesModal'
import WorkflowDetailsModal from './WorkflowDetailsModal'
import { DocQualityIssuesList } from '~/constants/aclLevel'
import { useEsignature } from '~/hooks/useEsignature'
import type { User } from '../../types/user'

// Type definitions
interface FileDetails {
  id: number
  name: string
  type: string
  favoriteName: string
  favoriteNameExt?: string
  favoriteNameExlExt?: string
  isFavorite?: boolean
  fsiTypeTemplate?: {
    id: number
    name: string
    value: number
    label: string
  }
  title?: string
  docNo?: number
  isBlindedDocument?: boolean
  owner?: {
    id: number
    name: string
    value?: number
  }
  ownerId?: number
  expiresAt?: number
  expiresAtDate?: any // moment object
  dueDate?: {
    dueDate?: number
    status?: string
  }
  dueDateValue?: any // moment object
  allowedDocumentTypeTemplates?: Array<{
    id: number
    name: string
    value: number
    label: string
    propertyType?: string | null
  }>
  documentTypesAllowedType?: string
  parent?: {
    id: number
    name: string
  }
  abstractParent?: {
    abstractParent: {
      id: number
      name: string
    }
  }
  workflow?: {
    id: number
    formattedId: string
  }
  symLinkFsiId?: number
  symLinkToVersionId?: number
  symLinkFvVersionId?: string
  amendments?: Array<{
    referenceFsiId: number
    filename: string
  }>
  systemFolderType?: string
  systemFolderTypeLocalized?: string
  acl?: string
  versionNo?: string
  state?: string
  rejectReason?: string
  comments?: string
  size?: number
  createdAt?: number
  createdBy?: {
    id: number
    name: string
  }
  updatedOn?: number
  updatedBy?: {
    id: number
    name: string
  }
  lastApprovers?: Array<{
    userFullName: string
    signatureDate: number
  }>
  valid?: boolean
  certifiedCopy?: boolean
  alternativeName?: string
  filePlan?: {
    name: string
  }
  placeholderLinkedDocuments?: any[]
  placeholderMaxDocuments?: number
  orginatingPlaceholder?: string
  effectiveFileVersionId?: number
  folderStructureTemplateType?: {
    name: string
  }
  obsolete?: boolean
  numberOfEffectiveDocumentsInFolder?: number
  numberOfEffectiveDocumentsInSubTree?: number
  sumTotalSizeOfEffectiveDocumentsInFolder?: number
  sumTotalSizeOfDocumentsInSubTree?: number
  expired?: boolean
  documentUID?: string
  templateAllowed?: boolean
  templateName?: string
  isMDF?: boolean
  MDFType?: string | null
  actionFlags?: {
    canEditDetails?: boolean
    canEditBlinded?: boolean
    canEditDueDate?: boolean
  }
  docQualityIssues?: string[]
  qcReviewStatus?: string
  lastQcReviewOn?: number
  periodicReviewCustomDateEnabled?: boolean
  lastPeriodicReviewDate?: number
  nextPeriodicReviewDate?: number
  sizeofDocumentsInSubTree?: any
  sizeofDocumentsInFolder?: any
}

interface VersionInfo {
  versionId?: number
  minorVersionId?: number
}

interface FolderAndDocumentDetailsModalProps {
  fileDetails: FileDetails
  versionInfo: VersionInfo
  onClose?: () => void
  onUpdate?: () => void // Callback to notify parent to refresh data
  isReport?: boolean
}

interface FormValues {
  details: {
    favoriteNameExlExt?: string
    isFavorite?: boolean
    fsiTypeTemplate?: {
      value: number
      label: string
    } | null
    title?: string
    docNo?: number
    isBlindedDocument?: boolean
    ownerId?: number | null
    expiresAtDate?: any
    dueDateValue?: any
    allowedDocumentTypeTemplates?: Array<{
      label: string
      value: number
      propertyType?: string | null
    }>
    isFolder?: boolean
    docQualityIssues?: string[]
    [key: string]: any
  }
}

interface SelectOption {
  value: number | string | boolean
  label: string
}

// Validation Schema
const ValidationSchema = () => {
  return yup.object().shape({
    details: yup.object().shape({
      favoriteNameExlExt: yup
        .string()
        .max(255)
        .nullable()
        .default(() => '')
        .when('isFavorite', {
          is: true,
          then: (schema: any) => schema.required()
        }),

      title: yup
        .string()
        .max(255)
        .nullable()
        .default(() => '')
        .when('isFolder', {
          is: false,
          then: (schema: any) => schema.required()
        }),
      expiresAtDate: yup
        .object()
        .nullable()
        .when('requireExpirationDate', {
          is: true,
          then: (schema: any) => schema.required()
        }),
      docNo: yup
        .number()
        .min(0)
        .nullable()
        .transform((value: any) => (isNaN(value) ? null : value))
        .default(() => 0)
        .required(),
      ownerId: yup.number().nullable().required(),
      fsiTypeTemplate: yup.object().nullable().required(),
      isBlindedDocument: yup
        .bool()
        .nullable()
        .default(() => false)
        .required()
    })
  })
}

const FolderAndDocumentDetailsModal: React.FC<FolderAndDocumentDetailsModalProps> = ({
  fileDetails,
  versionInfo,
  onClose,
  onUpdate,
  isReport = false
}) => {
  const context = useServiceContext()
  const { constant: Constants, service, api } = context
  const { toast, auth, access, filter } = service
  const { showModal } = useModal()
  const { signAction } = useEsignature()
  const user: User | null = service.auth.getUser?.() || null

  const {
    qcReview: { qcReviewEnabled } = { qcReviewEnabled: false },
    isEnableDocDetailsExpirationDate = false,
    allFeaturesData
  } = service?.init?.getFeaturesInfo?.() || {}

  const { translate, date, extension, trusted } = filter

  // Initialize texts factory for legacy compatibility
  const texts = textsFactory()

  // Access table constants for permission checks - matches legacy accessService.rightsTable.js GROUPS
  const accessTable = {
    rcmAdmins: ['ADMIN', 'CONTENT_ADMIN'],
    stagingManagers: ['ETMF_MANAGER', 'ETMF_SPECIALIST'],
    rcmUsers: ['POWER_USER', 'USER'],
    stagingUsers: ['ETMF_SITE_USER', 'ETMF_STUDY_USER']
  }

  // Date formatter wrapper to handle shortDate and shortTime formats
  const dateFormat = (value: any, format?: string | boolean): string => {
    if (!value) return ''

    if (format === 'shortDate') {
      return date(value, false)
    } else if (format === 'shortTime') {
      const fullDateTime = date(value, true)
      const shortDateValue = date(value, false)
      return fullDateTime.startsWith(`${shortDateValue} `)
        ? fullDateTime.slice(shortDateValue.length + 1)
        : fullDateTime
    } else if (typeof format === 'boolean') {
      return date(value, format)
    } else if (format) {
      return date(value)
    }
    return date(value)
  }

  // Helper function for async picker options
  const getOptionsForAsyncPicker = async (requestFn: any, params: any) => {
    try {
      const response = await requestFn(params)
      return {
        options:
          response.elements?.map((item: any) => ({
            value: item.id,
            label: item.name
          })) || [],
        hasMore: response.elements?.length > 0 && params.pageStart * params.pageSize < response.totalElements
      }
    } catch (error) {
      return { options: [], hasMore: false }
    }
  }

  const [isOpen, setIsOpen] = useState<boolean>(true)
  const [editingMode, setEditingMode] = useState<boolean>(false)
  const [editingModeCR, setEditingModeCR] = useState<boolean>(false)
  const [renameFavoriteMode, setRenameFavoriteMode] = useState<boolean>(false)
  const isFile = !isReport
  const [optionsUsers, setOptionsUsers] = useState<SelectOption[]>([])
  const [symLinkToFsi, setSymLinkToFsi] = useState<FileDetails | null>(null)
  const [originalFileDetails] = useState<any>({})
  const [defaultVals, setDefaultVals] = useState<any>({})
  const [originalExpiresAt, setOriginalExpiresAt] = useState<number | undefined>()
  const [fileDetailsState, setFileDetailsState] = useState<FileDetails>(fileDetails)
  const [isDocumentCountCalculating, setIsDocumentCountCalculating] = useState<boolean>(false)
  const [hasDocumentCountCalculated, setHasDocumentCountCalculated] = useState<boolean>(false)
  const [hideVersion, setHideVersion] = useState<boolean>(false)

  const optionsBlinded: SelectOption[] = [
    { value: true, label: translate('BLINDING_STATUS_BLINDED') },
    { value: false, label: translate('BLINDING_STATUS_UNBLINDED') }
  ]

  const docQualityIssuesListModified = DocQualityIssuesList.map((item: any) => ({
    ...item,
    label: translate(item?.label)
  }))
  const docQualityList = docQualityIssuesListModified.filter((item: any) =>
    fileDetails?.docQualityIssues?.includes(item?.value)
  )
  const isEtmfEnabled = service.init.getFeaturesInfo()?.etmfFeature?.show

  // Utility functions
  const bytesFormat = (value: number): string => {
    if (!value) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(value) / Math.log(k))
    return Math.round((value / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  const excerptFormat = (text: string, stripHtml: boolean = false, length: number = 100): string => {
    let processedText = text
    if (stripHtml) {
      processedText = text.replace(/<[^>]*>/g, '')
    }
    return processedText.length > length ? processedText.substring(0, length) + '...' : processedText
  }

  const capitalizeFormat = (text: string): string => {
    if (!text) return ''
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
  }

  const mimetypeFormat = (filename: string): string => {
    // Match legacy behavior: return en-dash (–) if no filename
    if (!filename) return '–'

    // Extract extension (match legacy ValidateFile._getFilePathExtension behavior)
    const ext = filename.split('.').pop()?.toLowerCase()
    if (!ext) return '–'

    // Match legacy MimeTypes mapping exactly
    // NOTE: jpg/jpeg/png are intentionally NOT included in "Images" to match legacy behavior
    const mimeTypesMap: Record<string, string> = {
      // Images (only these extensions, NOT jpg/jpeg/png - they're missing in legacy!)
      tiff: 'Images',
      gif: 'Images',
      bmp: 'Images',
      ico: 'Images',
      // CSV
      csv: 'Comma Separated Values',
      // Text
      txt: 'Text',
      // MS PowerPoint
      ppt: 'MS PowerPoint',
      pptx: 'MS PowerPoint',
      // MS Excel
      xls: 'MS Excel',
      xlsx: 'MS Excel',
      // MS Word
      doc: 'MS Word',
      docx: 'MS Word',
      // Adobe
      psd: 'Adobe Photoshop',
      eps: 'Encapsulated PostScript',
      pdf: 'Adobe Acrobat PDF'
    }

    // Return matched type name or en-dash (–) if not found (match legacy behavior)
    return mimeTypesMap[ext] || '–'
  }

  const numberFormat = (value: number): string => {
    if (typeof value !== 'number') return '0'
    return value.toLocaleString()
  }

  // Helper functions
  const processFsiDetailsData = (details: FileDetails): FileDetails => {
    const processedDetails = { ...details }

    if (
      [Constants.file.type.DOCUMENT, Constants.file.type.REFERENCE].includes(details.type) &&
      details.favoriteName &&
      details.favoriteName.indexOf('.') !== -1
    ) {
      processedDetails.favoriteNameExt = '.' + details.favoriteName.split('.').pop()
      const nameParts = details.favoriteName.split('.')
      nameParts.pop()
      processedDetails.favoriteNameExlExt = nameParts.join('.')
    } else {
      processedDetails.favoriteNameExlExt = details.favoriteName || ''
    }

    if (details.owner && details.owner.id) {
      processedDetails.owner = {
        ...details.owner,
        value: details.owner.id,
        name: details.owner.name
      }
      processedDetails.ownerId = details.owner.id
    }

    if (details.expiresAt) {
      processedDetails.expiresAtDate = moment(details.expiresAt)
    }

    if (details.dueDate && details.dueDate.dueDate) {
      processedDetails.dueDateValue = moment(details.dueDate.dueDate)
    }

    processedDetails.isMDF = details.isMDF
    processedDetails.MDFType = details.MDFType ? details.MDFType : null

    if (details.fsiTypeTemplate && details.fsiTypeTemplate.id) {
      processedDetails.fsiTypeTemplate = {
        ...details.fsiTypeTemplate,
        value: details.fsiTypeTemplate.id,
        label: details.fsiTypeTemplate.name
      }
    }

    return processedDetails
  }

  const getDefaults = (vals: any) => {
    const defaults = ValidationSchema().cast(
      { details: vals },
      {
        recursive: true,
        stripUnknown: false,
        context: { details: vals }
      }
    )
    return defaults
  }

  const parentIsWorkflowFolder = (): boolean => {
    const WF_MATCH_REGEX = /^WF(\d{5,})/
    return inWorkflow() && WF_MATCH_REGEX.test(fileDetails.parent?.name || '')
  }

  const inWorkflow = (): boolean => {
    return fileDetailsState.systemFolderType === Constants.file.systemFolderType.WORKFLOWS
  }

  const isEffectiveVersion = (): boolean => {
    return !(versionInfo && typeof versionInfo.versionId === 'number' && versionInfo.versionId >= 0)
  }

  const canEditBlindingMode = (): boolean => {
    return !!(
      fileDetailsState &&
      fileDetailsState.type === Constants.file.type.DOCUMENT &&
      fileDetailsState.actionFlags &&
      fileDetailsState.actionFlags.canEditBlinded
    )
  }

  const canEditDueDate = (): boolean => {
    return (
      !!fileDetailsState?.type &&
      [Constants.file.type.DOCUMENT, Constants.file.type.PLACEHOLDER].includes(fileDetailsState.type) &&
      !!fileDetailsState?.actionFlags?.canEditDueDate
    )
  }

  const getBlindedStatusTranslated = (): string => {
    return fileDetailsState && typeof fileDetailsState.isBlindedDocument !== 'undefined'
      ? translate(fileDetailsState.isBlindedDocument ? 'BLINDING_STATUS_BLINDED' : 'BLINDING_STATUS_UNBLINDED')
      : '—'
  }

  // Data loading
  const loadData = () => {
    getDetails(fileDetailsState.id, versionInfo.versionId, versionInfo.minorVersionId).then((data: any) => {
      const processedFsiDetails = processFsiDetailsData(data)
      setFileDetailsState(processedFsiDetails)
      setDefaultVals(getDefaults(processedFsiDetails))
    })
  }

  const openTypeDetailModal = async (id: number) => {
    try {
      if (fileDetailsState.type === Constants.file.type.FOLDER) {
        const typeData = (await api.folderTypes.getById(id)) as any
        showModal(<FolderModalEdit folderType={typeData} onClose={() => {}} />)
      } else {
        const typeData = (await api.documentTypes.getById(id)) as any
        showModal(<DocumentModalEdit documentType={typeData} onClose={() => {}} />)
      }
      doClose()
    } catch (error) {
      console.error('Error fetching type details:', error)
      toast.error(translate('ERROR_LOADING_TYPE_DETAILS'))
    }
  }

  const openWorkflowDetailModal = () => {
    if (fileDetailsState.workflow?.id) {
      showModal(<WorkflowDetailsModal wfId={fileDetailsState.workflow.id} onClose={() => {}} />)
    }
  }

  const openLinkedFilesModal = (e: React.MouseEvent) => {
    e.preventDefault()
    if (fileDetailsState.placeholderLinkedDocuments && fileDetailsState.placeholderLinkedDocuments.length > 0) {
      showModal(<LinkedFilesModal documentsList={fileDetailsState.placeholderLinkedDocuments} onClose={() => {}} />)
    }
  }

  const openUsersModal = () => {
    const txt = fileDetailsState.lastApprovers?.reduce((mem, user) => {
      return (
        mem +
        user.userFullName +
        ', ' +
        dateFormat(user.signatureDate, 'shortDate') +
        ' ' +
        dateFormat(user.signatureDate, 'shortTime') +
        '<br>'
      )
    }, '')
    confirmModal({
      titleMsg: translate('LAST_APPROVED_BY'),
      msg: <span dangerouslySetInnerHTML={{ __html: txt || '' }} />,
      cancelTxt: '',
      okTxt: translate('OK')
    })
  }

  const calculateFolderDocumentCount = () => {
    setIsDocumentCountCalculating(true)
    api.files
      .getNumbersofdocs(fileDetailsState.id)
      .then((data: any) => {
        setHasDocumentCountCalculated(true)
        // Extend fileDetailsState with the new data
        setFileDetailsState((prev) => ({ ...prev, ...data }))
      })
      .finally(() => {
        setIsDocumentCountCalculating(false)
      })
  }

  // Save operations
  const confirmDocTypesUsage = async (): Promise<boolean> => {
    return true
  }

  const renameFavoriteName = (values: FormValues): Promise<void> => {
    return new Promise((resolve, reject) => {
      const apiCall = renameFavorite(
        fileDetailsState.id,
        values.details.favoriteNameExlExt + (fileDetailsState.favoriteNameExt || '')
      )

      apiCall.then(
        () => {
          loadData()
          resolve()
          window.dispatchEvent(new Event('updateDashboard'))
          window.dispatchEvent(new Event('updateFavorites'))
          window.dispatchEvent(new CustomEvent('updateFiles', { detail: fileDetailsState.id }))
          if (onUpdate) {
            onUpdate() // Notify parent to refresh
          }
        },
        (data: any) => {
          if (data?.data?.code === 417) {
            toast.error(translate('UNDERLYINF_DATA_WERE_CHNGD_TRY_AGAIN'))
          } else {
            const errorMsg = data?.data?.message || data?.message || 'Unknown error'
            toast.error(translate('ERROR_ERROR', { error: errorMsg }))
          }
          reject()
        }
      )
    })
  }

  const updateFsiDetails = (modal: any, validation: any, values: FormValues): Promise<void> => {
    let newData: any = {}

    if (fileDetailsState.type === Constants.file.type.FOLDER) {
      newData = {
        owner: values.details.ownerId
      }
      if (
        fileDetailsState.documentTypesAllowedType ===
        Constants.system.settings.folderType.documentTypesAllowed.DEFINED_ON_FOLDER
      ) {
        newData.allowedDocumentTypeTemplateIds = values.details.allowedDocumentTypeTemplates?.map((item) => item.value)
      }
    } else {
      newData = {
        title: values.details.title,
        docNo: values.details.docNo,
        isBlindedDocument: values.details.isBlindedDocument
      }

      if (values.details.fsiTypeTemplate) {
        newData.fsiTypeTplId = values.details.fsiTypeTemplate.value
      }
      if (values.details.ownerId) {
        newData.owner = values.details.ownerId
      }
      if (values.details.docQualityIssues) {
        newData.docQualityIssues = values.details.docQualityIssues
      }
      if (fileDetailsState.effectiveFileVersionId) {
        newData.effectiveFileVersionId = fileDetailsState.effectiveFileVersionId
      }

      // Handle Expiration Date
      if (!values.details.expiresAtDate) {
        newData.expiresAt = null
      } else if (values.details.expiresAtDate.valueOf() !== fileDetailsState.expiresAt) {
        const utcDate = values.details.expiresAtDate
        utcDate.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        newData.expiresAt = utcDate.valueOf()
      } else {
        newData.expiresAt = fileDetailsState.expiresAt
      }

      // Handle Due Date
      if (!values.details.dueDateValue) {
        newData.dueDate = null
      } else if (values.details.dueDateValue.valueOf() !== fileDetailsState.dueDate?.dueDate) {
        const utcDate = values.details.dueDateValue
        utcDate.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        newData.dueDate = utcDate.valueOf()
      } else {
        newData.dueDate = fileDetailsState.dueDate?.dueDate
      }
    }

    if (fileDetailsState.isFavorite) {
      newData.favoriteName = values.details.favoriteNameExlExt + (fileDetailsState?.favoriteNameExt || '')
    }

    return new Promise((resolve, reject) => {
      const apiCall = changeFSIDetails(fileDetailsState.id, newData, validation)

      apiCall.then(
        () => {
          loadData()
          if (fileDetailsState.type !== Constants.file.type.FOLDER) {
            toast.success(translate('BROWSER_DOC_DETAILS_CHANGED_SUCCESS_MSG'))
          } else if (fileDetailsState.type === Constants.file.type.FOLDER) {
            toast.success(translate('BROWSER_FOLDER_DETAILS_CHANGED_SUCCESS_MSG'))
          }
          resolve()
          window.dispatchEvent(new Event('updateDashboard'))
          window.dispatchEvent(new Event('updateFavorites'))
          window.dispatchEvent(new CustomEvent('updateFiles', { detail: fileDetailsState.id }))
          if (onUpdate) {
            onUpdate() // Notify parent to refresh
          }
        },
        (data: any) => {
          if (data?.data?.code === 417) {
            toast.error(translate('UNDERLYINF_DATA_WERE_CHNGD_TRY_AGAIN'))
          } else {
            const errorMsg = data?.data?.message || data?.message || 'Unknown error'
            toast.error(translate(errorMsg))
          }
          reject()
        }
      )
    })
  }

  const confirmExpirationDate = (values: FormValues): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (values.details.expiresAtDate && values.details.expiresAtDate.valueOf() !== fileDetailsState.expiresAt) {
        const expiresAt = cloneDeep(values.details.expiresAtDate)
        expiresAt.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        const today = new Date()
        today.setHours(0, 0, 0, 0)

        if (expiresAt.valueOf() < today.getTime()) {
          confirmModal({
            cancelTxt: translate('NO'),
            okTxt: translate('YES'),
            titleMsg: translate('CONFIRMATION'),
            msg: translate('FSI_DETAILS_SAVE_EXPIRATION_DATE_PAST_CONFIRM_QUESTION'),
            onCancel: () => {
              reject()
            },
            onSubmit: () => {
              resolve()
            }
          })
        } else {
          resolve()
        }
      } else {
        resolve()
      }
    })
  }

  const confirmDueDate = (values: FormValues): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (values.details.dueDateValue && values.details.dueDateValue.valueOf() !== fileDetailsState.dueDate?.dueDate) {
        const dueDate = cloneDeep(values.details.dueDateValue)
        dueDate.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        const today = new Date()
        today.setHours(0, 0, 0, 0)

        if (dueDate.valueOf() < today.getTime()) {
          confirmModal({
            cancelTxt: translate('NO'),
            okTxt: translate('YES'),
            titleMsg: translate('CONFIRMATION'),
            msg: translate('FSI_DETAILS_SAVE_DUE_DATE_PAST_CONFIRM_QUESTION'),
            onCancel: () => {
              reject()
            },
            onSubmit: () => {
              resolve()
            }
          })
        } else {
          resolve()
        }
      } else {
        resolve()
      }
    })
  }

  const saveChanges = async (values: FormValues) => {
    if (await confirmDocTypesUsage()) {
      confirmExpirationDate(values).then(() => {
        confirmDueDate(values).then(async () => {
          if (!fileDetailsState.actionFlags?.canEditDetails) {
            doClose()
            return renameFavoriteName(values)
          } else {
            if (auth.requireESignatureForFileCommands()) {
              try {
                const validation = await signAction('ADMIN_ACTION', 'ADMIN_ACTION_DOCUMENT_DETAILS_CHANGED')
                doClose()
                return updateFsiDetails(null, validation, values)
              } catch (error) {
                // E-signature cancelled
                return
              }
            } else {
              doClose()
              return updateFsiDetails(null, null, values)
            }
          }
        })
      })
    } else {
      doClose()
    }
  }

  // Editing controls
  const startEditing = () => {
    if (!fileDetailsState.actionFlags?.canEditDetails) {
      setRenameFavoriteMode(true)
    } else {
      if (inWorkflow()) {
        setEditingModeCR(true)
      } else {
        setEditingMode(true)
      }
    }
  }

  const cancelEditing = () => {
    setRenameFavoriteMode(false)
    setEditingMode(false)
    setEditingModeCR(false)
    doClose()
  }

  const doClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  // Effects
  useEffect(() => {
    if (fileDetails) {
      const parsedDetails = processFsiDetailsData(fileDetails)
      setDefaultVals({
        favoriteNameExlExt: parsedDetails.favoriteNameExlExt,
        isFavorite: parsedDetails.isFavorite,
        fsiTypeTemplate: parsedDetails.fsiTypeTemplate,
        title: parsedDetails.title,
        docNo: parsedDetails.docNo,
        isBlindedDocument: parsedDetails.isBlindedDocument,
        ownerId: parsedDetails.ownerId,
        expiresAtDate: parsedDetails.expiresAtDate,
        dueDateValue: parsedDetails.dueDateValue,
        allowedDocumentTypeTemplates: parsedDetails.allowedDocumentTypeTemplates?.map((element) => ({
          label: element?.name,
          value: element?.id,
          propertyType: element?.propertyType ? element.propertyType : null
        })),
        isFolder: parsedDetails.type === Constants.file.type.FOLDER,
        docQualityIssues: docQualityList.map((item) => item.value)
      })
      setFileDetailsState(parsedDetails)
    }
  }, [fileDetails])

  useEffect(() => {
    // Load users for owner dropdown
    api.users.getAssembledUsersGroups().then((data: any) => {
      const userOptions =
        data.users?.map((user: any) => ({
          value: user.id,
          label: user.name
        })) || []
      setOptionsUsers(userOptions)
    })

    if (fileDetails.type === Constants.file.type.REFERENCE && fileDetails.symLinkFsiId) {
      getDetails(fileDetails.symLinkFsiId, versionInfo.versionId, versionInfo.minorVersionId).then((data: any) => {
        setSymLinkToFsi(data)
      })
    }
    setHideVersion(!!auth.getPolicies()?.hideStackedDocumentVersion)
  }, [])

  const popoverDocQualityInfo = (
    <OverlayTrigger
      trigger={['hover', 'click']}
      placement="right"
      overlay={
        <Popover id="9eb98d67-3044-4183-98f8-9b7e402b26ed">
          <div className="d-flex">
            <i className="ai-icon-purple-gradient"></i>
            <div dangerouslySetInnerHTML={{ __html: translate('DOC_DETAILS_FILE_WARNING_POPOVER') }}></div>
          </div>
        </Popover>
      }
    >
      <span className="warning-file-upload-updated"></span>
    </OverlayTrigger>
  )

  return (
    <>
      <Formik
        validate={(values: FormValues) => {
          return ValidationSchema()
            .validate(values, {
              abortEarly: false,
              context: values
            })
            .then(
              () => {
                return {}
              },
              (err: any) => {
                return yupToFormErrors(err)
              }
            )
        }}
        enableReinitialize
        onReset={(values: FormValues) => {
          values.details = { ...defaultVals }
        }}
        onSubmit={(values: FormValues, actions: any) => {
          const finalData = getDefaults(values.details)
          const type = cloneDeep(finalData)
          return saveChanges(values)
        }}
        initialValues={{
          details: { ...defaultVals }
        }}
      >
        {({ values, setFieldValue, handleSubmit, dirty }) => (
          <Modal
            className="theme-sandman"
            show={isOpen}
            handleClose={doClose}
            size="md"
            title={
              isReport
                ? translate('BROWSER_REPORT_DETAILS_H_TITLE')
                : fileDetailsState.type === Constants.file.type.FOLDER
                  ? translate('BROWSER_FOLDER_DETAILS_H_TITLE')
                  : fileDetailsState.isMDF
                    ? translate('RCM_MASTER_DATA_FILE_DETAILS_H_TITLE')
                    : translate('BROWSER_DOC_DETAILS_H_TITLE')
            }
          >
            <Modal.Body>
              {fileDetailsState.type !== Constants.file.type.FOLDER && (
                <>
                  <table className="file-details">
                    <tbody>
                      {/* Name */}
                      <tr id="bfd68c7f-1396-4191-b963-c9c5b5493cd8">
                        <td className="param">{translate('NAME')}</td>
                        <td className="value">
                          {(isFile || isReport) && <span className="whitespaceprewrap">{fileDetailsState.name}</span>}
                        </td>
                      </tr>

                      {/* Favorite Name */}
                      {fileDetailsState.isFavorite && (
                        <tr id="22c127b1-53d0-4345-b7b2-bf8ff579c59a">
                          <td className="param">{translate('FSI_FAVORITE_NAME_LABEL')}</td>
                          {!editingMode && !renameFavoriteMode && (
                            <td className="value whitespaceprewrap">{fileDetailsState.favoriteName}</td>
                          )}
                          {(editingMode || renameFavoriteMode) && (
                            <td className="value pt1 form-group">
                              <FormGroup
                                controlId="a2c724b4-f892-44a5-be68-5c9370a2f7fb"
                                className={'mb0 w100p pos-relative' + (fileDetailsState.favoriteNameExt ? ' w88p' : '')}
                              >
                                <Field
                                  type="text"
                                  autoFocus
                                  name="details.favoriteNameExlExt"
                                  id="a2c724b4-f892-44a5-be68-5c9370a2f7fb"
                                />
                                <ErrorMessage name="details.favoriteNameExlExt" />
                                {fileDetailsState.favoriteNameExt && (
                                  <span className="ext-txt">{fileDetailsState.favoriteNameExt}</span>
                                )}
                              </FormGroup>
                            </td>
                          )}
                        </tr>
                      )}

                      {/* Parent Folder */}
                      <tr id="3ee8b340-d9f7-439c-8d9e-e55495c31b92">
                        <td className="param">{translate('PARENT_FOLDER')}</td>
                        <td className="value">
                          {auth.isStagingUploader() ? (
                            <div>
                              {fileDetailsState.abstractParent ? (
                                <span className="word-break whitespaceprewrap">
                                  {fileDetailsState.abstractParent.abstractParent.name}
                                </span>
                              ) : (
                                <span className="word-break whitespaceprewrap">
                                  {fileDetailsState.parent ? fileDetailsState.parent?.name : translate('NOT_AVAILABLE')}
                                </span>
                              )}
                            </div>
                          ) : (
                            <div>
                              {parentIsWorkflowFolder() ? (
                                <span className="word-break whitespaceprewrap">{fileDetailsState.parent?.name}</span>
                              ) : fileDetailsState.abstractParent ? (
                                <Link
                                  stateName="browser"
                                  stateParams={{
                                    folder: fileDetailsState.abstractParent.abstractParent.id,
                                    type: (fileDetailsState.abstractParent.abstractParent as any).type,
                                    page: 1
                                  }}
                                  className="word-break whitespaceprewrap"
                                  onClick={doClose}
                                >
                                  {fileDetailsState.abstractParent.abstractParent.name}
                                </Link>
                              ) : fileDetailsState.parent ? (
                                <Link
                                  stateName="browser"
                                  stateParams={{
                                    folder: fileDetailsState.parent?.id,
                                    type: fileDetailsState.systemFolderType,
                                    page: 1
                                  }}
                                  className="word-break whitespaceprewrap"
                                  onClick={doClose}
                                >
                                  {fileDetailsState.parent?.name}
                                </Link>
                              ) : (
                                <span>{translate('NOT_AVAILABLE')}</span>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>

                      {/* Type (Document/Report) */}
                      {(isReport || fileDetailsState.type !== Constants.file.type.REFERENCE) && (
                        <tr id="3722b2d7-ec69-48a6-8de4-a8f11728786b">
                          <td className="param">{translate('TYPE')}</td>
                          <td
                            className={
                              'value' +
                              (editingMode && fileDetailsState.fsiTypeTemplate && fileDetailsState.fsiTypeTemplate.value
                                ? ' pt1'
                                : '')
                            }
                          >
                            {isFile && (
                              <div>
                                {editingMode ? (
                                  fileDetailsState.fsiTypeTemplate?.value && (
                                    <FormGroup className="mb0" controlId="3ea9ad5e-08f0-4a13-b270-e355814f9d74">
                                      <InfiniteScrollSelect
                                        placeholder={translate('SELECT_DOC_TYPES_PLACEHOLDER...')}
                                        name="details.fsiTypeTemplate"
                                        value={values.details.fsiTypeTemplate}
                                        asyncLoadFn={async (search: string, page: number) => {
                                          const response = await api.documentTypes.getOptionsForAsyncPicker(
                                            search,
                                            page
                                          )
                                          return {
                                            options: response.options,
                                            hasMore: response.hasMore
                                          }
                                        }}
                                        onChange={(option: any) => {
                                          setFieldValue('details.fsiTypeTemplate', option)
                                        }}
                                        searchable
                                        isClearable={false}
                                        id="3ea9ad5e-08f0-4a13-b270-e355814f9d74"
                                      />
                                      <ErrorMessage name="details.fsiTypeTemplate" />
                                    </FormGroup>
                                  )
                                ) : (
                                  <div>
                                    {fileDetailsState.fsiTypeTemplate ? (
                                      service.access.isAllowed('settingsAccess') ? (
                                        <TextLinkButton
                                          action={() => openTypeDetailModal(fileDetailsState.fsiTypeTemplate!.value)}
                                        >
                                          {fileDetailsState.fsiTypeTemplate.label}
                                        </TextLinkButton>
                                      ) : (
                                        <span className="grayed-text">{fileDetailsState.fsiTypeTemplate.label}</span>
                                      )
                                    ) : (
                                      translate('NOT_AVAILABLE')
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                            {isReport && (
                              <div>
                                {fileDetailsState.systemFolderTypeLocalized
                                  ? fileDetailsState.systemFolderTypeLocalized
                                  : translate('NOT_AVAILABLE')}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}

                      {/* Template warnings/notes */}
                      {isFile && !fileDetailsState.templateAllowed && fileDetailsState.templateName && (
                        <tr id="d64494fd-445e-4b55-9151-bd0812dd3360">
                          <td></td>
                          <td>
                            <div className="note-info">
                              {translate('BROWSER_FSI_DETAILS_EDIT_WARNING', {
                                templateName: fileDetailsState.templateName
                              })}
                            </div>
                          </td>
                        </tr>
                      )}
                      {isFile && editingMode && fileDetailsState.templateAllowed && !fileDetailsState.templateName && (
                        <tr id="bcb87c66-8284-4ba7-8f0d-a4406b7f05da">
                          <td></td>
                          <td>
                            <div className="note-info mb10">
                              {translate(
                                fileDetailsState.isMDF ? 'MDF_DETAILS_EDIT_NOTE' : 'BROWSER_FSI_DETAILS_EDIT_NOTE'
                              )}
                            </div>
                          </td>
                        </tr>
                      )}

                      {/* Amendments */}
                      {isFile && fileDetailsState.amendments && fileDetailsState.amendments.length > 0 && (
                        <tr id="2a9a0cff-a0ba-46e8-a27a-627d17562e33">
                          <td className="param">{translate('ASSOCIATED_AMENDMENTS')}</td>
                          <td className="value">
                            {fileDetailsState.amendments.map((item, index) => (
                              <Link
                                stateName="show-file"
                                stateParams={{
                                  file: item.referenceFsiId
                                }}
                                key={index}
                                title={item.filename}
                                onClick={doClose}
                              >
                                {' '}
                                {excerptFormat(item.filename, false, 60) +
                                  (index < fileDetailsState.amendments!.length - 1 ? ',' : '')}
                              </Link>
                            ))}
                          </td>
                        </tr>
                      )}

                      {/* Workflow/CR Number */}
                      <tr id="f50bc1e6b-7b27-4a35-99ba-d0663e008cbd">
                        <td className="param">{translate('WORKFLOW_CHANGE_REQUEST_NUMBER')}</td>
                        <td className="value">
                          {fileDetailsState.workflow ? (
                            service.access.isAllowed('workflowsAccessDetail') ? (
                              <TextLinkButton action={openWorkflowDetailModal}>
                                {fileDetailsState.workflow.formattedId}
                              </TextLinkButton>
                            ) : (
                              <span>{fileDetailsState.workflow.formattedId}</span>
                            )
                          ) : (
                            <span>{translate('NOT_AVAILABLE')}</span>
                          )}
                        </td>
                      </tr>

                      {/* Title */}
                      {isFile &&
                        [Constants.file.type.DOCUMENT, Constants.file.type.PLACEHOLDER].includes(
                          fileDetailsState.type
                        ) && (
                          <tr id="8c41da13-0258-467c-9d60-f27290f41cbf">
                            <td className="param">{translate('TITLE_DOCUMENT')}</td>
                            {(editingMode || editingModeCR) && (
                              <td className="value pt1">
                                <FormGroup className="w100p" controlId="bc465105-c4a1-450b-9959-ae315465e9f8">
                                  <Field type="text" name="details.title" id="bc465105-c4a1-450b-9959-ae315465e9f8" />
                                  <ErrorMessage name="details.title" />
                                </FormGroup>
                              </td>
                            )}
                            {!editingMode && !editingModeCR && (
                              <td className="value whitespaceprewrap">{fileDetailsState.title}</td>
                            )}
                          </tr>
                        )}

                      {/* Document Number */}
                      {isFile && fileDetailsState.type === Constants.file.type.DOCUMENT && !fileDetailsState.isMDF && (
                        <tr id="b5a97627-902f-4fed-b533-ed6150c7c025">
                          <td className="param">{translate('DOCUMENT_NUMBER')}</td>
                          {editingMode ? (
                            <td className="value pt1">
                              <FormGroup className="w100p" controlId="39500818-9b3d-4fb1-8123-0852b63746b4">
                                <Field type="number" name="details.docNo" id="39500818-9b3d-4fb1-8123-0852b63746b4" />
                                <ErrorMessage name="details.docNo" />
                              </FormGroup>
                            </td>
                          ) : (
                            <td className="value">{fileDetailsState.docNo}</td>
                          )}
                        </tr>
                      )}

                      {/* Document UID */}
                      {isFile &&
                        fileDetailsState.type === Constants.file.type.DOCUMENT &&
                        !access.isAllowed('EXTERNAL') &&
                        !access.isAllowed('INSPECTOR') && (
                          <tr id="e5baa4af-5d77-4209-8b8e-07b8ffdac2ae">
                            <td className="param">{translate('DOCUMENT_IDENTIFIER')}</td>
                            <td className="value">
                              {fileDetailsState.documentUID
                                ? fileDetailsState.documentUID === '-'
                                  ? translate('NOT_AVAILABLE')
                                  : fileDetailsState.documentUID
                                : translate('NOT_AVAILABLE')}
                            </td>
                          </tr>
                        )}

                      {/* Version */}
                      {isFile &&
                        fileDetailsState.type === Constants.file.type.DOCUMENT &&
                        !fileDetailsState.isMDF &&
                        !service.access.isAllowed(['EXTERNAL'] as any) &&
                        !hideVersion && (
                          <tr id="825afcda-5733-4056-83aa-620bd0d1c010">
                            <td className="param">{translate('VERSION')}</td>
                            <td className="value">
                              <span>
                                {isEffectiveVersion()
                                  ? fileDetailsState.versionNo && fileDetailsState.versionNo !== '-'
                                    ? fileDetailsState.versionNo
                                    : translate('NOT_AVAILABLE')
                                  : versionInfo.versionId + '.' + versionInfo.minorVersionId}
                              </span>
                            </td>
                          </tr>
                        )}

                      {/* State */}
                      {isFile && !fileDetailsState.isMDF && (
                        <tr id="57978c25-b3c0-489f-8dde-fd2c2e752d7f">
                          <td className="param">{translate('STATE')}</td>
                          <td className="value">
                            {fileDetailsState.state ? (
                              Constants.file.lifecycleState[
                                fileDetailsState.state as keyof typeof Constants.file.lifecycleState
                              ] ? (
                                <span>{capitalizeFormat(translate('STATE_' + fileDetailsState.state))}</span>
                              ) : (
                                <span>{capitalizeFormat(fileDetailsState.state)}</span>
                              )
                            ) : (
                              <span>{translate('NOT_AVAILABLE')}</span>
                            )}
                          </td>
                        </tr>
                      )}

                      {/* Reject Reason */}
                      {fileDetailsState.rejectReason && (
                        <tr id="4254e380-5da9-4e0e-aa25-dcb00f3d09f7">
                          <td className="param">{translate('TASK_REJECTION_REASON_LABEL')}</td>
                          <td className="value">{fileDetailsState.rejectReason}</td>
                        </tr>
                      )}

                      {/* Comment */}
                      {fileDetailsState.rejectReason && (
                        <tr id="f82125a1-1439-4981-b2e2-bea6b4f96866">
                          <td className="param">{translate('COMMENT')}</td>
                          <td className="value">
                            {fileDetailsState.comments ? fileDetailsState.comments : translate('NOT_AVAILABLE')}
                          </td>
                        </tr>
                      )}

                      {/* Access Rights */}
                      {isFile && (
                        <tr id="a951bbf3-8f8c-4329-af67-df79926e3e14">
                          <td className="param">{translate('ACCESS_RIGHTS')}</td>
                          <td className="value">{translate('ACL_' + fileDetailsState.acl)}</td>
                        </tr>
                      )}

                      {/* Blinding Status */}
                      {isFile &&
                        fileDetailsState.acl &&
                        [
                          Constants.file.acl.VIEW,
                          Constants.file.acl.READ,
                          Constants.file.acl.WRITE,
                          Constants.file.acl.DELETE
                        ].indexOf(fileDetailsState.acl) > -1 && (
                          <tr id="b8ad67e0-f444-4908-aa76-5b845572ab82">
                            <td className="param">{translate('BLINDING_STATUS_LABEL')}</td>
                            {(editingMode || editingModeCR) && canEditBlindingMode() && (
                              <td className="value pt1">
                                <FormGroup className="mb0" controlId="89cbd275-622d-401e-b1aa-b1319017e9d0">
                                  <Select
                                    name="details.isBlindedDocument"
                                    value={values.details.isBlindedDocument}
                                    options={optionsBlinded}
                                    onChange={(option: any) => {
                                      setFieldValue('details.isBlindedDocument', option ? option.value : null)
                                    }}
                                    id="89cbd275-622d-401e-b1aa-b1319017e9d0"
                                    isClearable={false}
                                  />
                                  <ErrorMessage name="details.isBlindedDocument" />
                                </FormGroup>
                              </td>
                            )}
                            {((!editingMode && !editingModeCR) || !canEditBlindingMode()) && (
                              <td className="value">{getBlindedStatusTranslated()}</td>
                            )}
                          </tr>
                        )}

                      {/* Validation Status */}
                      {fileDetailsState.type !== Constants.file.type.FOLDER && (
                        <tr id="c609b836-8da2-458b-966b-24adaea0e4c6">
                          <td className="param">{translate('VALIDATION_STATUS')}</td>
                          <td className="value">
                            {translate(
                              fileDetailsState.valid ? 'VALIDATION_STATUS_VALID' : 'VALIDATION_STATUS_INVALID'
                            )}
                          </td>
                        </tr>
                      )}

                      {/* Size */}
                      {fileDetailsState.type === Constants.file.type.DOCUMENT && !fileDetailsState.isMDF && (
                        <tr id="a4ed9894-4356-4a13-a659-ac4ae26bf3f8">
                          <td className="param">{translate('SIZE')}</td>
                          <td className="value">
                            {fileDetailsState.size ? bytesFormat(fileDetailsState.size) : translate('NOT_AVAILABLE')}
                          </td>
                        </tr>
                      )}

                      {/* Owner */}
                      <tr id="0163d74a-cc69-48ae-be5c-f4b020d0adf2">
                        <td className="param">{translate('OWNER')}</td>
                        {editingMode ? (
                          <td className="value pt1">
                            <FormGroup className="mb0" controlId="498326a3-ec08-4b1a-8f9b-720ae0f4e6d1">
                              <Select
                                name="details.ownerId"
                                value={values.details.ownerId}
                                options={optionsUsers}
                                onChange={(option: any) => {
                                  setFieldValue('details.ownerId', option ? option.value : null)
                                }}
                                placeholder={translate('SELECT_USER...')}
                                searchable
                                isClearable={false}
                                id="498326a3-ec08-4b1a-8f9b-720ae0f4e6d1"
                              />
                              <ErrorMessage name="details.ownerId" />
                            </FormGroup>
                          </td>
                        ) : (
                          <td className="value">
                            {fileDetailsState.owner ? fileDetailsState.owner.name : translate('NOT_AVAILABLE')}
                          </td>
                        )}
                      </tr>

                      {/* Format */}
                      <tr id="f50f894c-8141-4671-af4e-f2ffdb16bda2">
                        <td className="param">{translate('FORMAT')}</td>
                        {fileDetailsState.type === Constants.file.type.DOCUMENT && !fileDetailsState.isMDF && (
                          <td className="value">{mimetypeFormat(fileDetailsState.name)}</td>
                        )}
                        {fileDetailsState.type === Constants.file.type.PLACEHOLDER && (
                          <td className="value">{translate('FSI_TYPE_PLACEHOLDER')}</td>
                        )}
                        {fileDetailsState.type === Constants.file.type.REFERENCE && (
                          <td className="value">{translate('FSI_TYPE_REFERENCE')}</td>
                        )}
                      </tr>

                      {/* File Plan */}
                      {fileDetailsState.filePlan && (
                        <tr id="9ca3be37-f143-461e-be52-d14a662dd052">
                          <td className="param">{translate('FILE_PLAN')}</td>
                          <td className="value">
                            {fileDetailsState.filePlan.name
                              ? fileDetailsState.filePlan.name
                              : translate('NOT_AVAILABLE')}
                          </td>
                        </tr>
                      )}

                      {/* Linked Documents (Placeholder) */}
                      {fileDetailsState.type === Constants.file.type.PLACEHOLDER && (
                        <tr id="4dac6a69-0eb8-421b-b0bf-36bf09099988">
                          <td className="param">{translate('LINKED_DOCUMENTS')}</td>
                          <td className="value">
                            {fileDetailsState.placeholderLinkedDocuments &&
                            fileDetailsState.placeholderLinkedDocuments.length > 0 ? (
                              <a href="#" onClick={openLinkedFilesModal}>
                                <span>
                                  {translate('LINKED_DOCUMENTS_MULTI', {
                                    docsLinked: fileDetailsState.placeholderLinkedDocuments.length,
                                    docsMax:
                                      fileDetailsState.placeholderMaxDocuments === -1
                                        ? translate('UNLIMITED')
                                        : fileDetailsState.placeholderMaxDocuments
                                  })}
                                </span>
                              </a>
                            ) : (
                              <span>
                                {translate('LINKED_DOCUMENTS_MULTI', {
                                  docsLinked: fileDetailsState.placeholderLinkedDocuments?.length || 0,
                                  docsMax:
                                    fileDetailsState.placeholderMaxDocuments === -1
                                      ? translate('UNLIMITED')
                                      : fileDetailsState.placeholderMaxDocuments
                                })}
                              </span>
                            )}
                          </td>
                        </tr>
                      )}

                      {/* Originating Placeholder */}
                      {fileDetailsState.orginatingPlaceholder && (
                        <tr id="7cf7367c-54e5-4bde-b986-18ff7bac6215">
                          <td className="param">{translate('ORIGINATING_PLACEHOLDER')}</td>
                          <td className="value">{fileDetailsState.orginatingPlaceholder}</td>
                        </tr>
                      )}

                      {/* Certified Copy */}
                      {isFile && !fileDetailsState.isMDF && (
                        <tr id="0bd7bee1-91a2-4d7f-a927-7474f5fc88d5">
                          <td className="param">{translate('FSI_DETAILS_IS_CERTIFIED_COPY')}</td>
                          <td className="value">{translate(fileDetailsState.certifiedCopy ? 'YES' : 'NO')}</td>
                        </tr>
                      )}

                      {/* Alternative Name */}
                      {fileDetailsState.alternativeName && (
                        <tr id="6a0cb753-ab25-4074-a774-013567c81b88">
                          <td className="param">{translate('FSI_DETAILS_ALTERNATIVE_NAME')}</td>
                          <td className="value">{fileDetailsState.alternativeName}</td>
                        </tr>
                      )}

                      {/* Expiration Date */}
                      {isFile &&
                        [Constants.file.type.DOCUMENT].includes(fileDetailsState.type) &&
                        !fileDetailsState.isMDF &&
                        isEnableDocDetailsExpirationDate && (
                          <tr id="43bc1716-e7e5-492d-bd19-182ae19d6331">
                            <td className="param">{translate('EXPIRATION_DATE')}</td>
                            <td className="value">
                              {editingMode || editingModeCR ? (
                                <FormGroup className="mb0" controlId="3c0fc98b-a2a9-48bf-8dd1-544ddf28bddd">
                                  <DatePicker
                                    name="details.expiresAtDate"
                                    selected={values.details.expiresAtDate}
                                    onChange={(val: any) => {
                                      setFieldValue('details.expiresAtDate', val)
                                    }}
                                    popoverTargetOffset="3px 0"
                                    todayButton="Today"
                                    dateFormatCalendar="MMMM"
                                    dateFormat={getDatePickerParseFormats()}
                                    showYearDropdown
                                    className="form-control field-control w100p"
                                    id="3c0fc98b-a2a9-48bf-8dd1-544ddf28bddd"
                                  />
                                  <ErrorMessage name="details.expiresAtDate" />
                                </FormGroup>
                              ) : (
                                <div>
                                  {fileDetailsState.expiresAt
                                    ? formatUserDateTime(fileDetailsState.expiresAt)
                                    : translate('NOT_AVAILABLE')}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}

                      {/* QC Review Status */}
                      {qcReviewEnabled && (
                        <>
                          <tr id="9beea4ba-0467-43fb-af1d-ee14685a5b95">
                            <td className="param">{translate('QC_REVIEW_STATUS_FILTER')}</td>
                            <td className="value">
                              {fileDetailsState?.qcReviewStatus
                                ? translate(parseQCDocStatus(fileDetailsState.qcReviewStatus) || 'QC_STATUS_UNKNOWN')
                                : '-'}
                            </td>
                          </tr>
                          {!!fileDetailsState?.lastQcReviewOn && (
                            <tr id="4ea9e1e3-8917-4ecf-b643-dc0f75e65634">
                              <td className="param">{translate('LAST_QC_REVIEW_ON_TITLE')}</td>
                              <td className="value">
                                <span>{formatUserDateTime(fileDetailsState.lastQcReviewOn)}</span>
                              </td>
                            </tr>
                          )}
                        </>
                      )}

                      {/* Document Quality Issues */}
                      {isEtmfEnabled &&
                        fileDetailsState?.docQualityIssues &&
                        fileDetailsState.docQualityIssues.length >= 0 &&
                        access.isAllowed([
                          accessTable.rcmAdmins,
                          accessTable.stagingManagers,
                          accessTable.rcmUsers,
                          accessTable.stagingUsers
                        ] as any) &&
                        [AclLevel.BROWSE, AclLevel.VIEW, AclLevel.READ, AclLevel.WRITE, AclLevel.DELETE].includes(
                          fileDetailsState?.acl as any
                        ) && (
                          <tr id="32227f1b-0020-4b92-b84d-70f356c3093a">
                            <td className="param">{translate('DOCUMENT_QUALITY_ISSUE')}</td>
                            {access.isAllowed([
                              accessTable.rcmAdmins,
                              'ETMF_MANAGER',
                              'POWER_USER',
                              'ETMF_SPECIALIST'
                            ] as any) &&
                            [AclLevel.READ, AclLevel.WRITE, AclLevel.DELETE].includes(fileDetailsState?.acl as any) &&
                            editingMode &&
                            fileDetailsState?.docQualityIssues?.length > 0 ? (
                              <td className="value pt1">
                                <FormGroup className="mb0" controlId="fea844ee-78c6-4edb-93e5-a06a184c2431">
                                  <Select
                                    name="details.docQualityIssues"
                                    value={values.details.docQualityIssues}
                                    options={docQualityIssuesListModified}
                                    onChange={(option: any) => {
                                      setFieldValue(
                                        'details.docQualityIssues',
                                        option ? option.map((o: any) => o.value) : []
                                      )
                                    }}
                                    searchable
                                    isMulti
                                    isOptionDisabled={(option: any) => {
                                      return docQualityIssuesListModified
                                        .map((item) => item.value)
                                        .includes(option.value)
                                    }}
                                    isClearable={false}
                                    id="fea844ee-78c6-4edb-93e5-a06a184c2431"
                                  />
                                </FormGroup>
                              </td>
                            ) : (
                              <td>
                                {fileDetailsState?.docQualityIssues?.length > 0 && <span>{popoverDocQualityInfo}</span>}
                                <td className="value">
                                  {fileDetailsState?.docQualityIssues?.length > 0
                                    ? docQualityList.map((item) => item.label).join(', ')
                                    : translate('NOT_AVAILABLE')}
                                </td>
                              </td>
                            )}
                          </tr>
                        )}

                      {/* Created On */}
                      <tr id="3399bd97-570e-4ce4-a434-e8659c37f100">
                        <td className="param">{translate('CREATED_ON')}</td>
                        {fileDetailsState.createdAt && (
                          <td className="value">
                            <span>{formatUserDateTime(fileDetailsState.createdAt)}</span>{' '}
                            <span>
                              {translate('BY_USERNAME', {
                                name: fileDetailsState.createdBy
                                  ? fileDetailsState.createdBy.name
                                  : translate('SYSTEM_USER')
                              })}
                            </span>
                          </td>
                        )}
                      </tr>

                      {/* Due Date (eTMF) */}
                      {isEtmfEnabled &&
                        [Constants.file.type.PLACEHOLDER, Constants.file.type.DOCUMENT].includes(
                          fileDetailsState.type
                        ) &&
                        fileDetailsState.dueDate && (
                          <tr id="due-date-field">
                            <td className="param">{translate('DUE_DATE')}</td>
                            <td className="value">
                              {(editingMode || editingModeCR) && canEditDueDate() ? (
                                <FormGroup className="mb0" controlId="due-date-picker">
                                  <DatePicker
                                    name="details.dueDateValue"
                                    selected={values.details.dueDateValue}
                                    onChange={(val: any) => {
                                      setFieldValue('details.dueDateValue', val)
                                    }}
                                    popoverTargetOffset="3px 0"
                                    todayButton="Today"
                                    dateFormatCalendar="MMMM"
                                    dateFormat={getDatePickerParseFormats()}
                                    showYearDropdown
                                    className="form-control field-control w100p"
                                    id="due-date-picker"
                                  />
                                  <ErrorMessage name="details.dueDateValue" />
                                </FormGroup>
                              ) : (
                                <div>
                                  {fileDetailsState.dueDate && fileDetailsState.dueDate.dueDate ? (
                                    <>
                                      {formatUserDateTime(fileDetailsState.dueDate.dueDate, false)}
                                      {isEtmfEnabled &&
                                        fileDetailsState.type === Constants.file.type.PLACEHOLDER &&
                                        fileDetailsState.dueDate.status && (
                                          <DueDateIndicator
                                            dueDateStatus={fileDetailsState.dueDate.status}
                                            translate={translate}
                                          />
                                        )}
                                    </>
                                  ) : (
                                    translate('NOT_AVAILABLE')
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}

                      {/* Last Approvers */}
                      {fileDetailsState.lastApprovers && fileDetailsState.lastApprovers.length > 0 && (
                        <tr id="f93357dc-6630-442c-b113-709a1969ab15">
                          <td className="param">{translate('LABEL_APPROVED_ON')}</td>
                          <td className="value">
                            {fileDetailsState.lastApprovers.length > 1 ? (
                              <TextLinkButton action={openUsersModal}>{translate('MULTIPLE_APPROVERS')}</TextLinkButton>
                            ) : (
                              <div>
                                <span>{formatUserDateTime(fileDetailsState.lastApprovers[0].signatureDate)}</span>{' '}
                                {fileDetailsState.lastApprovers[0].userFullName && (
                                  <span>
                                    {translate('BY_USERNAME', {
                                      name: fileDetailsState.lastApprovers[0].userFullName
                                    })}
                                  </span>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}

                      {/* Updated On */}
                      <tr id="377b0c14-8cde-48c5-8bc8-6f251f44177e">
                        <td className="param">{translate('LABEL_UPDATED_ON')}</td>
                        <td className="value">
                          {fileDetailsState.updatedOn && isEffectiveVersion() ? (
                            <div>
                              <span>{formatUserDateTime(fileDetailsState.updatedOn)}</span>{' '}
                              <span>
                                {translate('BY_USERNAME', {
                                  name: fileDetailsState.updatedBy
                                    ? fileDetailsState.updatedBy.name
                                    : translate('SYSTEM_USER')
                                })}
                              </span>
                            </div>
                          ) : (
                            <span>{translate('NOT_AVAILABLE')}</span>
                          )}
                        </td>
                      </tr>

                      {/* Periodic Review Dates */}
                      {fileDetailsState.periodicReviewCustomDateEnabled && (
                        <>
                          {fileDetailsState.lastPeriodicReviewDate && (
                            <tr>
                              <td className="param">{translate('LAST_PERIODIC_REVIEW_DATE')}</td>
                              <td className="value">
                                {formatUserDateTime(fileDetailsState.lastPeriodicReviewDate).split(' ')[0]}
                              </td>
                            </tr>
                          )}
                          {fileDetailsState.nextPeriodicReviewDate && (
                            <tr>
                              <td className="param">{translate('NEXT_PERIODIC_REVIEW_DATE')}</td>
                              <td className="value">
                                {formatUserDateTime(fileDetailsState.nextPeriodicReviewDate).split(' ')[0]}
                              </td>
                            </tr>
                          )}
                        </>
                      )}
                    </tbody>
                  </table>

                  {/* Destination Document (Reference/Symlink) */}
                  {fileDetailsState.type === Constants.file.type.REFERENCE && symLinkToFsi && (
                    <div className="destination-document-section">
                      <h4>{translate('DESTINATION_DOCUMENT')}</h4>
                      <table className="file-details">
                        <tbody>
                          {/* Destination Document: Filename */}
                          <tr id="destination-doc-filename">
                            <td className="param">{translate('FILENAME')}</td>
                            <td className="value">
                              <Link stateName="show-file" stateParams={{ fileId: symLinkToFsi.id }} onClick={doClose}>
                                {symLinkToFsi.name}
                              </Link>
                            </td>
                          </tr>

                          {/* Destination Document: Folder */}
                          {symLinkToFsi.parent && (
                            <tr id="destination-doc-folder">
                              <td className="param">{translate('FOLDER')}</td>
                              <td className="value">
                                <Link
                                  stateName="browser"
                                  stateParams={{
                                    folder: symLinkToFsi.parent.id,
                                    type: symLinkToFsi.systemFolderType,
                                    page: 1
                                  }}
                                  onClick={doClose}
                                >
                                  {symLinkToFsi.parent.name}
                                </Link>
                              </td>
                            </tr>
                          )}

                          {/* Destination Document: Type */}
                          <tr id="destination-doc-type">
                            <td className="param">{translate('TYPE')}</td>
                            <td className="value">
                              {symLinkToFsi.fsiTypeTemplate
                                ? symLinkToFsi.fsiTypeTemplate.name
                                : translate('NOT_AVAILABLE')}
                            </td>
                          </tr>

                          {/* Destination Document: Version */}
                          <tr id="destination-doc-version">
                            <td className="param">{translate('VERSION')}</td>
                            <td className="value">{fileDetailsState.symLinkFvVersionId}</td>
                          </tr>

                          {/* Destination Document: Expiration Date */}
                          {isEnableDocDetailsExpirationDate ? (
                            <tr id="destination-doc-expiration">
                              <td className="param">{translate('EXPIRATION_DATE')}</td>
                              <td className="value">
                                {symLinkToFsi.expiresAt
                                  ? formatUserDateTime(symLinkToFsi.expiresAt)
                                  : translate('NOT_AVAILABLE')}
                              </td>
                            </tr>
                          ) : null}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}

              {/* Folder Details Table */}
              {fileDetailsState.type === Constants.file.type.FOLDER && (
                <table className="file-details">
                  <tbody>
                    <tr>
                      <td className="param">{translate('NAME')}</td>
                      <td className="value whitespaceprewrap">{fileDetailsState.name}</td>
                    </tr>

                    {fileDetailsState.isFavorite && (
                      <tr>
                        <td className="param">{translate('FSI_FAVORITE_NAME_LABEL')}</td>
                        {!renameFavoriteMode && (
                          <td className="value whitespaceprewrap">{fileDetailsState.favoriteName}</td>
                        )}
                        {renameFavoriteMode && (
                          <td className="value pt1">
                            <FormGroup className="mb0 w100p pos-relative">
                              <Field type="text" autoFocus name="details.favoriteNameExlExt" />
                              <ErrorMessage name="details.favoriteNameExlExt" />
                            </FormGroup>
                          </td>
                        )}
                      </tr>
                    )}

                    <tr>
                      <td className="param">{translate('PARENT_FOLDER')}</td>
                      <td className="value">
                        {auth.isStagingUploader() ? (
                          <span className="word-break whitespaceprewrap">
                            {fileDetailsState.parent ? fileDetailsState.parent.name : translate('NOT_AVAILABLE')}
                          </span>
                        ) : fileDetailsState.parent ? (
                          <Link
                            stateName="browser"
                            stateParams={{
                              folder: fileDetailsState.parent.id,
                              type: fileDetailsState.systemFolderType,
                              page: 1
                            }}
                            className="word-break whitespaceprewrap"
                            onClick={doClose}
                          >
                            {fileDetailsState.parent.name}
                          </Link>
                        ) : (
                          <span>{translate('NOT_AVAILABLE')}</span>
                        )}
                      </td>
                    </tr>

                    <tr>
                      <td className="param">{translate('TYPE')}</td>
                      <td className="value">
                        {fileDetailsState.fsiTypeTemplate ? (
                          service.access.isAllowed('settingsAccess') ? (
                            <TextLinkButton action={() => openTypeDetailModal(fileDetailsState.fsiTypeTemplate!.value)}>
                              {fileDetailsState.fsiTypeTemplate.label}
                            </TextLinkButton>
                          ) : (
                            <span>{fileDetailsState.fsiTypeTemplate.label}</span>
                          )
                        ) : (
                          translate('NOT_AVAILABLE')
                        )}
                      </td>
                    </tr>

                    {fileDetailsState.folderStructureTemplateType && (
                      <tr>
                        <td className="param">{translate('FOLDER_STRUCTURE_TEMPLATE_TYPE')}</td>
                        <td className="value">{fileDetailsState.folderStructureTemplateType.name}</td>
                      </tr>
                    )}

                    <tr>
                      <td className="param">{translate('STATE')}</td>
                      <td className="value">
                        <span>{translate(fileDetailsState.obsolete ? 'STATE_OBSOLETE' : 'STATE_ACTIVE')}</span>
                      </td>
                    </tr>

                    <tr>
                      <td className="param">{translate('ACCESS_RIGHTS')}</td>
                      <td className="value">{translate('ACL_' + fileDetailsState.acl)}</td>
                    </tr>

                    {fileDetailsState.documentTypesAllowedType ===
                      Constants.system.settings.folderType.documentTypesAllowed.DEFINED_ON_FOLDER && (
                      <tr>
                        <td className="param">{translate('BROWSER_FOLDER_DETAILS_ALLOWED_DOCUMENT_TYPES')}</td>
                        <td className={'value' + (editingMode ? ' pt1' : '')}>
                          {editingMode ? (
                            <FormGroup className="mb0">
                              <InfiniteScrollSelect
                                placeholder={translate('SELECT_DOC_TYPES_PLACEHOLDER...')}
                                name="details.allowedDocumentTypeTemplates"
                                value={values.details.allowedDocumentTypeTemplates}
                                asyncLoadFn={async (search: string, page: number) => {
                                  const response = await api.documentTypes.getOptionsForAsyncPicker(search, page)
                                  return {
                                    options: response.options,
                                    hasMore: response.hasMore
                                  }
                                }}
                                onChange={(option: any) => {
                                  setFieldValue('details.allowedDocumentTypeTemplates', option)
                                }}
                                searchable
                                isMulti
                              />
                              <ErrorMessage name="details.allowedDocumentTypeTemplates" />
                            </FormGroup>
                          ) : fileDetailsState.allowedDocumentTypeTemplates &&
                            fileDetailsState.allowedDocumentTypeTemplates.length > 0 ? (
                            fileDetailsState.allowedDocumentTypeTemplates.map((docType) => docType.name).join(', ')
                          ) : (
                            translate('NOT_AVAILABLE')
                          )}
                        </td>
                      </tr>
                    )}

                    <tr>
                      <td className="param">{translate('OWNER')}</td>
                      {editingMode ? (
                        <td className="value pt1">
                          <FormGroup className="mb0">
                            <Select
                              name="details.ownerId"
                              value={values.details.ownerId}
                              options={optionsUsers}
                              onChange={(option: any) => {
                                setFieldValue('details.ownerId', option ? option.value : null)
                              }}
                              placeholder={translate('SELECT_USER...')}
                              searchable
                              isClearable={false}
                            />
                            <ErrorMessage name="details.ownerId" />
                          </FormGroup>
                        </td>
                      ) : (
                        <td className="value">
                          {fileDetailsState.owner ? fileDetailsState.owner.name : translate('NOT_AVAILABLE')}
                        </td>
                      )}
                    </tr>

                    {/* Document Count Section - Only for ADMIN users */}
                    {user?.role === 'ADMIN' && (
                      <tr id="1fe60979-2a63-4ce4-b363-ed42fc8a17ad">
                        <td className="param">{translate('FSI_DETAILS_NUMBER_OF_DOCUMENTS')}</td>
                        <td className="value">
                          {hasDocumentCountCalculated ? (
                            <div>
                              <span>
                                {numberFormat(fileDetailsState.numberOfEffectiveDocumentsInFolder || 0) +
                                  ' ' +
                                  translate('FSI_DETAILS_NUMBER_OF_DOCUMENTS_IN_CURRENT')}
                              </span>
                              <span> / </span>
                              <span>
                                {numberFormat(fileDetailsState.numberOfEffectiveDocumentsInSubTree || 0) +
                                  ' ' +
                                  translate('FSI_DETAILS_NUMBER_OF_DOCUMENTS_INCL_SUBFOLDERS')}
                              </span>
                            </div>
                          ) : (
                            <button
                              onClick={calculateFolderDocumentCount}
                              className="btn btn-chita btn-second"
                              disabled={isDocumentCountCalculating}
                            >
                              {translate(
                                isDocumentCountCalculating
                                  ? 'CALCULATING_FOLDER_DOCUMENT_COUNT'
                                  : 'CALCULATE_FOLDER_DOCUMENT_COUNT'
                              )}
                            </button>
                          )}
                        </td>
                      </tr>
                    )}

                    {user?.role === 'ADMIN' && (
                      <tr id="4472a1da-546e-479d-a391-0db978d909de">
                        <td className="param">{translate('FSI_DETAILS_SIZE_OF_DOCUMENTS')}</td>
                        <td className="value">
                          {hasDocumentCountCalculated && (
                            <div>
                              <span>
                                {bytesFormat(fileDetailsState.sumTotalSizeOfEffectiveDocumentsInFolder || 0) +
                                  ' ' +
                                  translate('FSI_DETAILS_SIZE_OF_DOCUMENTS_IN_CURRENT')}
                              </span>
                              <span>/</span>
                              <span>
                                {bytesFormat(fileDetailsState.sumTotalSizeOfDocumentsInSubTree || 0) +
                                  ' ' +
                                  translate('FSI_DETAILS_SIZE_OF_DOCUMENTS_INCL_SUBFOLDERS')}
                              </span>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}

                    <tr>
                      <td className="param">{translate('CREATED_ON')}</td>
                      {fileDetailsState.createdAt && (
                        <td className="value">
                          <span>{formatUserDateTime(fileDetailsState.createdAt)}</span>{' '}
                          {fileDetailsState.createdBy && (
                            <span>
                              {translate('BY_USERNAME', {
                                name: fileDetailsState.createdBy.name
                              })}
                            </span>
                          )}
                        </td>
                      )}
                    </tr>

                    <tr>
                      <td className="param">{translate('LABEL_UPDATED_ON')}</td>
                      <td className="value">
                        {fileDetailsState.updatedOn ? (
                          <div>
                            <span>{formatUserDateTime(fileDetailsState.updatedOn)}</span>{' '}
                            {fileDetailsState.updatedBy && (
                              <span>
                                {translate('BY_USERNAME', {
                                  name: fileDetailsState.updatedBy.name
                                })}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span>{translate('NOT_AVAILABLE')}</span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}
            </Modal.Body>
            <Modal.Footer>
              {!isReport &&
                auth.getRole() != Constants.user.roles.ETMF_SPONSOR &&
                (fileDetailsState.actionFlags?.canEditDetails || fileDetailsState.isFavorite) &&
                !editingMode &&
                !editingModeCR &&
                !renameFavoriteMode && (
                  <Button
                    data-action={'edit'}
                    className="btn btn-primary"
                    onClick={startEditing}
                    id="c0ae7a93-a370-414c-9952-724e9146d840"
                  >
                    {translate('EDIT')}
                  </Button>
                )}
              {!editingMode && !editingModeCR && !renameFavoriteMode && (
                <Button
                  data-action={'ok'}
                  className="btn btn-primary"
                  onClick={doClose}
                  id="dce0f68b-a2cb-461a-9a4d-cfe05eed6bdd"
                >
                  {translate('OK')}
                </Button>
              )}
              {(renameFavoriteMode || editingMode || editingModeCR) && (
                <Button data-action={'cancel'} onClick={cancelEditing} id="faf4cb3e-55e6-4fe6-85f9-93e6934da0f4">
                  {translate('CANCEL')}
                </Button>
              )}
              {(renameFavoriteMode || editingMode || editingModeCR) && (
                <Button
                  data-action={'save'}
                  className="btn btn-primary"
                  disabled={!dirty}
                  onClick={() => handleSubmit()}
                  id="618dc4b2-566f-4451-a342-375090b117aa"
                >
                  {translate('SAVE_BTN')}
                </Button>
              )}
            </Modal.Footer>
          </Modal>
        )}
      </Formik>
    </>
  )
}

export default FolderAndDocumentDetailsModal
